import styles from "./file-chooser.css?inline"
import html from "./file-chooser.html?raw"
import { sharedStyles } from "../../shared-styles"

const mimeTypesByExtension: Record<string, string> = {
  ".ttl": "text/turtle",
}

function getMimeType(file: File): string | undefined {
  if (file.type) {
    return file.type
  }
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
  return mimeTypesByExtension[ext]
}

interface FileChangeEventDetail {
  file: File
}

export interface FileChangeEvent extends CustomEvent {
  detail: FileChangeEventDetail
}

export function showFileSize(number: number): string {
  if (number < 1024) {
    return number + " bytes"
  } else if (number > 1024 && number < 1048576) {
    return (number / 1024).toFixed(1) + "KB"
  } else {
    return (number / 1048576).toFixed(1) + "MB"
  }
}

class FileChooser extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["no-preview"]
  }

  attributeChangedCallback(
    name: string,
    _oldValue: string | null,
    newValue: string | null,
  ) {
    if (name === "no-preview") {
      const preview = this.shadowRoot?.querySelector<HTMLElement>("#preview")
      if (preview) preview.style.display = newValue === null ? "" : "none"
    }
  }

  constructor() {
    super()
    const template = document.createElement("template")
    template.innerHTML = `
      <style>${styles}</style>
      ${html}
    `
    const shadow = this.attachShadow({ mode: "open" })
    shadow.adoptedStyleSheets = [sharedStyles]
    shadow.appendChild(template.content.cloneNode(true))
  }

  setPreview(file: { name: string; size: number; sourceUrl?: string }) {
    const base = `${file.name} (${showFileSize(file.size)})`
    if (file.sourceUrl) {
      let hostname = file.sourceUrl
      try {
        hostname = new URL(file.sourceUrl).hostname
      } catch {
        // sourceUrl is not parseable; fall back to the raw string.
      }
      this.writePreview(`${base} — ${hostname}`, file.sourceUrl)
    } else {
      this.writePreview(base)
    }
  }

  resetPreview() {
    this.writePreview("No file chosen")
  }

  private writePreview(text: string, title?: string) {
    const preview = this.shadowRoot!.querySelector<HTMLElement>("#preview")!
    preview.firstChild?.remove()
    const p = document.createElement("p")
    p.textContent = text
    if (title) p.title = title
    preview.appendChild(p)
  }

  connectedCallback() {
    const root = this.shadowRoot!
    const input = root.querySelector<HTMLInputElement>("#file-input")!

    input.style.opacity = "0"
    input.style.position = "absolute"
    input.style.zIndex = "-1"
    input.style.pointerEvents = "none"

    input.addEventListener("change", () => {
      if (input.files !== null && input.files.length > 0) {
        const [inputFile] = input.files
        const mimeType = getMimeType(inputFile)
        const file = mimeType
          ? new File([inputFile], inputFile.name, { type: mimeType })
          : inputFile
        this.setPreview(file)
        const event = new CustomEvent<FileChangeEventDetail>("change", {
          detail: { file },
        })
        this.dispatchEvent(event)
      } else {
        this.resetPreview()
      }
    })
  }
}
customElements.define("file-chooser", FileChooser)
export { FileChooser }
